// Lib
import { useCallback, useLayoutEffect, useState } from 'react';

// Types
import {
    INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS,
    type BackgroundRemovalProgress,
} from '../../../backgroundRemoval/imageModalBackgroundRemovalProgressTypes';
import {
    IMAGE_MODAL_V2_OPERATION_MESSAGE,
    type ImageModalV2OperationToastState,
} from '../../toolbar/imageModalV2OperationToastTypes';

const IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE = {
    IDLE: 'idle',
    REMOVING_BACKGROUND: 'removingBackground',
    UPLOADING_PROCESSED_IMAGE: 'uploadingProcessedImage',
    WAITING_FOR_EDITOR_IMAGE: 'waitingForEditorImage',
} as const;

type ImageModalV2BackgroundRemovalPhase =
    (typeof IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE)[keyof typeof IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE];

type EditorImageReloadState = {
    initialImageLoadKey: string;
};

const getBackgroundRemovalPhase = ({
    backgroundRemovalApiProgress,
    backgroundRemovalUploadProgress,
    editorImageReload,
}: {
    backgroundRemovalApiProgress: BackgroundRemovalProgress | null;
    backgroundRemovalUploadProgress: BackgroundRemovalProgress | null;
    editorImageReload: EditorImageReloadState | null;
}): ImageModalV2BackgroundRemovalPhase => {
    if (backgroundRemovalUploadProgress) return IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.UPLOADING_PROCESSED_IMAGE;
    if (editorImageReload) return IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.WAITING_FOR_EDITOR_IMAGE;
    if (backgroundRemovalApiProgress) return IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.REMOVING_BACKGROUND;

    return IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.IDLE;
};

const getBackgroundRemovalToast = ({
    phase,
}: {
    phase: ImageModalV2BackgroundRemovalPhase;
}): ImageModalV2OperationToastState | null => {
    if (phase === IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.IDLE) return null;

    return {
        message: IMAGE_MODAL_V2_OPERATION_MESSAGE.REMOVING_BACKGROUND,
        progress: INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS,
    };
};

const getBackgroundRemovalButtonProgress = ({
    phase,
    backgroundRemovalApiProgress,
    backgroundRemovalUploadProgress,
}: {
    phase: ImageModalV2BackgroundRemovalPhase;
    backgroundRemovalApiProgress: BackgroundRemovalProgress | null;
    backgroundRemovalUploadProgress: BackgroundRemovalProgress | null;
}): BackgroundRemovalProgress | null => {
    if (backgroundRemovalApiProgress || backgroundRemovalUploadProgress) {
        return backgroundRemovalUploadProgress || backgroundRemovalApiProgress;
    }

    if (phase === IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.WAITING_FOR_EDITOR_IMAGE)
        return INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS;

    return null;
};

/**
 * Models the v2 background-removal workflow as one phase rather than separate UI booleans.
 *
 * The operation toast deliberately stays on one indeterminate "Removing background" state while
 * the internal phase advances through provider processing, upload and editor image replacement.
 */
export const useImageModalV2BackgroundRemovalState = ({
    editorImageLoadKey,
    editorIsReady,
    backgroundRemovalApiProgress,
    backgroundRemovalUploadProgress,
}: {
    editorImageLoadKey: string;
    editorIsReady: boolean;
    backgroundRemovalApiProgress: BackgroundRemovalProgress | null;
    backgroundRemovalUploadProgress: BackgroundRemovalProgress | null;
}) => {
    const [hasLoadedEditorImage, setHasLoadedEditorImage] = useState(false);
    const [editorImageReload, setEditorImageReload] = useState<EditorImageReloadState | null>(null);
    const [decodedImageLoadKey, setDecodedImageLoadKey] = useState<string | null>(null);
    const [processedImageReady, setProcessedImageReady] = useState(false);

    useLayoutEffect(() => {
        if (!backgroundRemovalUploadProgress) return;

        setEditorImageReload(
            (current) =>
                current || {
                    initialImageLoadKey: editorImageLoadKey,
                },
        );
        setProcessedImageReady(false);
    }, [backgroundRemovalUploadProgress, editorImageLoadKey]);

    useLayoutEffect(() => {
        if (!editorImageReload) return;
        if (backgroundRemovalUploadProgress) return;
        if (editorImageReload.initialImageLoadKey === editorImageLoadKey) return;
        if (decodedImageLoadKey !== editorImageLoadKey) return;
        setProcessedImageReady(true);
    }, [backgroundRemovalUploadProgress, decodedImageLoadKey, editorImageLoadKey, editorImageReload]);

    const handleEditorImageReady = useCallback((ready: boolean) => {
        if (!ready) return;

        setHasLoadedEditorImage(true);
    }, []);

    const markProcessedImageDecoded = useCallback(() => {
        setDecodedImageLoadKey(editorImageLoadKey);
    }, [editorImageLoadKey]);

    const completeEditorImageReplacement = useCallback(() => {
        setEditorImageReload(null);
        setDecodedImageLoadKey(null);
        setProcessedImageReady(false);
    }, []);

    const phase = getBackgroundRemovalPhase({
        backgroundRemovalApiProgress,
        backgroundRemovalUploadProgress,
        editorImageReload,
    });
    const isProcessing = phase !== IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.IDLE;
    const shouldDeferEditorImageCommit = !!editorImageReload && !processedImageReady;

    return {
        phase,
        buttonProgress: getBackgroundRemovalButtonProgress({
            phase,
            backgroundRemovalApiProgress,
            backgroundRemovalUploadProgress,
        }),
        controlsDisabled: isProcessing,
        completeEditorImageReplacement,
        handleEditorImageReady,
        markProcessedImageDecoded,
        shouldAnimateEditorImageReplacement: isProcessing || shouldDeferEditorImageCommit,
        shouldDeferEditorImageCommit,
        shouldHideToolbar: isProcessing,
        shouldShowEditorLoadingPreview: !editorIsReady && !hasLoadedEditorImage,
        toast: getBackgroundRemovalToast({
            phase,
        }),
    };
};
